import { NumberInput } from '@zorro/design-system/v2/NumberInput';
import { Slider } from '@zorro/design-system/v2/Slider';
import { Title } from '@zorro/design-system/v2/Title';
import {
  formatCurrencyEnUs,
  parseNumberToFloat,
} from '@zorro/shared/formatters';
import { Flex, Stack } from '@zorro/web/design-system';
import Decimal from 'decimal.js';
import { Controller, UseFormReturn } from 'react-hook-form';

import { CombinedNotice } from './CombinedNotice';
import { PaymentMethodFormFields } from './usePaymentMethodForm';

type Props = {
  paymentMethodForm: UseFormReturn<PaymentMethodFormFields>;
};

export const CreateCombinedPaymentMethod = ({ paymentMethodForm }: Props) => {
  const { control, getValues, setValue } = paymentMethodForm;

  const premiumAmount = getValues('premiumAmount');

  const minValue = 1;
  const maxValue = premiumAmount - minValue;
  const step = 0.01;
  const precision = 2;
  const inputMaw = '7rem';

  return (
    <Stack>
      <Title order={2} size="1rem">
        Payment amount distribution
      </Title>
      <Flex wrap="nowrap" gap="md" align="center" mb="xl">
        <Controller
          control={control}
          name="zorroPayAmount"
          render={({ field: { onChange, ...rest } }) => (
            <NumberInput
              {...rest}
              flex="1 0 auto"
              label="Zorro Pay"
              prefix="$"
              min={minValue}
              max={maxValue}
              step={step}
              clampBehavior="strict"
              onChange={(newZorroPayValue) => {
                const number = parseNumberToFloat(newZorroPayValue);
                const hasConversionFailed = Number.isNaN(number);
                if (hasConversionFailed) {
                  return;
                }
                onChange(number);
                const selfPayAmount = new Decimal(premiumAmount).sub(number);
                setValue('selfPayAmount', selfPayAmount.toNumber());
              }}
              fixedDecimalScale
              decimalScale={precision}
              thousandSeparator
              maw={inputMaw}
            />
          )}
        />
        <Controller
          control={control}
          name="selfPayAmount"
          render={({ field: { onChange, ...rest } }) => (
            <Slider
              {...rest}
              min={minValue}
              max={maxValue}
              precision={precision}
              step={step}
              label={(value) => formatCurrencyEnUs(value)}
              onChange={(number) => {
                onChange(number);
                const zorroPayAmount = new Decimal(premiumAmount).sub(number);
                setValue('zorroPayAmount', zorroPayAmount.toNumber());
              }}
              px="xl"
              mt="xl"
              w="100%"
            />
          )}
        />
        <Controller
          control={control}
          name="selfPayAmount"
          render={({ field: { onChange, ...rest } }) => (
            <NumberInput
              {...rest}
              label="Self-pay"
              prefix="$"
              flex="1 0 auto"
              min={minValue}
              max={maxValue}
              step={step}
              clampBehavior="strict"
              onChange={(newSelfPayValue) => {
                const number = parseNumberToFloat(newSelfPayValue);
                const hasConversionFailed = Number.isNaN(number);
                if (hasConversionFailed) {
                  return;
                }
                onChange(number);
                setValue('zorroPayAmount', premiumAmount - number);
              }}
              fixedDecimalScale
              decimalScale={precision}
              thousandSeparator
              maw={inputMaw}
            />
          )}
        />
      </Flex>
      <CombinedNotice />
    </Stack>
  );
};
